import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle, TrendingDown, TrendingUp, Star, Info } from 'lucide-react';
import { formatCurrency, formatHours } from '@/lib/budget-utils';
import { CascadeTargetResolutionDialog } from './cascade-target-resolution-dialog';
import type { BudgetRevision, RevisionCascadeRecord } from '@/types/budget.types';
import type { BudgetRevisionSettings } from '@/types/resource-budget-settings.types';
import type { WhatIfImpact } from '@/hooks/use-what-if-budget';
import type { ProjectType } from '@/types';

interface RevisionCascadeConfirmDialogProps {
  open: boolean;
  revision: Partial<BudgetRevision>;
  impact: WhatIfImpact | null;
  cascadeSettings: BudgetRevisionSettings;
  isSaving?: boolean;
  projectType?: ProjectType;
  isCostCentreMode?: boolean;
  onConfirm: (cascadeRecord: RevisionCascadeRecord) => void;
  onCancel: () => void;
}

export function RevisionCascadeConfirmDialog({
  open,
  revision,
  impact,
  cascadeSettings,
  isSaving = false,
  projectType,
  isCostCentreMode = false,
  onConfirm,
  onCancel,
}: RevisionCascadeConfirmDialogProps) {
  // Pre-populate cascade checkboxes from org settings, but allow override
  const [updateCostLabour, setUpdateCostLabour] = useState(
    cascadeSettings.autoUpdateCostLabour
  );
  const [updateSalesLabour, setUpdateSalesLabour] = useState(
    cascadeSettings.autoUpdateSalesLabour && projectType === 'time-and-materials'
  );

  // Target resolution dialog state (second step)
  const [showTargetResolution, setShowTargetResolution] = useState(false);
  const [pendingCascadeRecord, setPendingCascadeRecord] = useState<RevisionCascadeRecord | null>(null);

  const salesCascadeAllowed = projectType === 'time-and-materials';

  const orgDefaultsBothOn =
    cascadeSettings.autoUpdateCostLabour && cascadeSettings.autoUpdateSalesLabour;
  const neitherSelected = !updateCostLabour && !updateSalesLabour;
  const showNeitherWarning = orgDefaultsBothOn && neitherSelected;

  const handleConfirm = () => {
    const cascadeRecord: RevisionCascadeRecord = {
      costBudgetUpdated: updateCostLabour,
      salesBudgetUpdated: updateSalesLabour,
      costLabourChange: updateCostLabour && impact ? impact.costLabourDelta : undefined,
      salesLabourChange: updateSalesLabour && impact ? impact.salesLabourDelta : undefined,
      overriddenByUser:
        updateCostLabour !== cascadeSettings.autoUpdateCostLabour ||
        updateSalesLabour !== (cascadeSettings.autoUpdateSalesLabour && salesCascadeAllowed),
    };

    // If neither cascade is checked, no target resolution needed
    if (!updateCostLabour && !updateSalesLabour) {
      onConfirm(cascadeRecord);
      return;
    }

    // In Cost Centre mode the backend resolves targets by cost centre code,
    // so skip the category target resolution dialog.
    if (isCostCentreMode) {
      onConfirm(cascadeRecord);
      return;
    }

    // Open the target resolution dialog as a second step
    setPendingCascadeRecord(cascadeRecord);
    setShowTargetResolution(true);
  };

  const handleTargetResolved = (targetCategoryId: number, targetCategoryName: string) => {
    if (pendingCascadeRecord) {
      const enrichedRecord: RevisionCascadeRecord = {
        ...pendingCascadeRecord,
        cascadeTargetCategoryId: targetCategoryId,
        cascadeTargetCategoryName: targetCategoryName,
      };
      setShowTargetResolution(false);
      setPendingCascadeRecord(null);
      onConfirm(enrichedRecord);
    }
  };

  const handleTargetCancelled = () => {
    setShowTargetResolution(false);
    setPendingCascadeRecord(null);
  };

  const formatSignedCurrency = (value: number) => {
    const abs = formatCurrency(Math.abs(value));
    return value >= 0 ? `+${abs}` : `-${abs}`;
  };

  const marginDelta = impact
    ? impact.newMargin - impact.previousMargin
    : 0;

  const marginColour =
    marginDelta >= 0 ? 'text-success-600' : marginDelta > -5 ? 'text-warning-600' : 'text-destructive';

  return (
    <>
      <Dialog open={open && !showTargetResolution} onOpenChange={(o) => !o && onCancel()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Confirm Budget Revision</DialogTitle>
            <DialogDescription>
              Review the details below before saving this revision.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Revision summary */}
            <div className="rounded-lg border p-4 space-y-2 bg-muted/30">
              <div className="flex items-center gap-2 flex-wrap">
                {revision.reasonCategoryLabel && (
                  <Badge variant="secondary">{revision.reasonCategoryLabel}</Badge>
                )}
                {revision.isBaseline && (
                  <Badge variant="outline" className="gap-1 text-warning-600 border-warning-400">
                    <Star className="h-3 w-3" />
                    Baseline: {revision.baselineName ?? 'New Baseline'}
                  </Badge>
                )}
              </div>

              {revision.reason && (
                <p className="text-sm text-muted-foreground">{revision.reason}</p>
              )}

              {impact && impact.hasChanges && (
                <div className="flex gap-4 pt-1 text-sm">
                  <span className="flex items-center gap-1">
                    {impact.totalHoursDelta >= 0 ? (
                      <TrendingUp className="h-3.5 w-3.5 text-warning-600" />
                    ) : (
                      <TrendingDown className="h-3.5 w-3.5 text-success-600" />
                    )}
                    <span className="font-medium">
                      {impact.totalHoursDelta >= 0 ? '+' : ''}
                      {formatHours(impact.totalHoursDelta)} hrs
                    </span>
                  </span>
                  <span className={`font-medium ${marginColour}`}>
                    Margin: {impact.newMargin.toFixed(1)}%
                    {marginDelta !== 0 && (
                      <span className="text-xs ml-1">
                        ({marginDelta >= 0 ? '+' : ''}{marginDelta.toFixed(1)}pts)
                      </span>
                    )}
                  </span>
                </div>
              )}
            </div>

            {/* Cascade section */}
            <div className="space-y-3">
              <p className="text-sm font-medium">Labour Budget Cascade</p>
              <p className="text-xs text-muted-foreground">
                Choose which budget types should have their Labour line updated to match this revision.
              </p>

              <div className="space-y-3">
                {/* Cost Labour */}
                <div className="flex items-start gap-3 rounded-md border p-3">
                  <Checkbox
                    id="cascade-cost"
                    checked={updateCostLabour}
                    onCheckedChange={(c) => setUpdateCostLabour(c as boolean)}
                  />
                  <div className="flex-1 space-y-0.5">
                    <Label htmlFor="cascade-cost" className="cursor-pointer font-medium text-sm">
                      Update Labour in Cost Budget
                    </Label>
                    {impact && impact.costLabourDelta !== 0 && (
                      <p className={`text-xs font-medium ${impact.costLabourDelta >= 0 ? 'text-warning-600' : 'text-success-600'}`}>
                        {formatSignedCurrency(impact.costLabourDelta)}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Org default: {cascadeSettings.autoUpdateCostLabour ? 'On' : 'Off'}
                    </p>
                  </div>
                </div>

                {/* Sales Labour */}
                <div className={`flex items-start gap-3 rounded-md border p-3 ${!salesCascadeAllowed ? 'opacity-50' : ''}`}>
                  <Checkbox
                    id="cascade-sales"
                    checked={updateSalesLabour}
                    onCheckedChange={(c) => setUpdateSalesLabour(c as boolean)}
                    disabled={!salesCascadeAllowed}
                  />
                  <div className="flex-1 space-y-0.5">
                    <Label htmlFor="cascade-sales" className={`font-medium text-sm ${salesCascadeAllowed ? 'cursor-pointer' : 'cursor-not-allowed'}`}>
                      Update Labour in Sales Budget
                    </Label>
                    {salesCascadeAllowed && impact && impact.salesLabourDelta !== 0 && (
                      <p className={`text-xs font-medium ${impact.salesLabourDelta >= 0 ? 'text-warning-600' : 'text-success-600'}`}>
                        {formatSignedCurrency(impact.salesLabourDelta)}
                      </p>
                    )}
                    {salesCascadeAllowed ? (
                      <p className="text-xs text-muted-foreground">
                        Org default: {cascadeSettings.autoUpdateSalesLabour ? 'On' : 'Off'}
                      </p>
                    ) : (
                      <div className="flex items-center gap-1 mt-0.5">
                        <Info className="h-3 w-3 text-muted-foreground" />
                        <p className="text-xs text-muted-foreground">
                          Sales Budget cascade is only available for Time &amp; Materials projects.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {showNeitherWarning && (
                <Alert className="border-warning-400 bg-warning-50 dark:bg-warning-950">
                  <AlertTriangle className="h-4 w-4 text-warning-600" />
                  <AlertDescription className="text-warning-800 dark:text-warning-200 text-sm">
                    Labour budgets will not be updated. Your org settings recommend updating both.
                  </AlertDescription>
                </Alert>
              )}
            </div>

            {/* Financial impact summary */}
            {impact && impact.hasChanges && (
              <div className="rounded-lg border p-3 space-y-2 text-sm">
                <p className="font-medium text-xs text-muted-foreground uppercase tracking-wide">
                  Financial Impact Preview
                </p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                  <span className="text-muted-foreground">Hours change</span>
                  <span className="font-medium text-right">
                    {impact.totalHoursDelta >= 0 ? '+' : ''}
                    {formatHours(impact.totalHoursDelta)}
                  </span>

                  {updateCostLabour && (
                    <>
                      <span className="text-muted-foreground">Cost Labour Δ</span>
                      <span className={`font-medium text-right ${impact.costLabourDelta >= 0 ? 'text-warning-600' : 'text-success-600'}`}>
                        {formatSignedCurrency(impact.costLabourDelta)}
                      </span>
                    </>
                  )}

                  {updateSalesLabour && (
                    <>
                      <span className="text-muted-foreground">Sales Labour Δ</span>
                      <span className={`font-medium text-right ${impact.salesLabourDelta >= 0 ? 'text-warning-600' : 'text-success-600'}`}>
                        {formatSignedCurrency(impact.salesLabourDelta)}
                      </span>
                    </>
                  )}

                  <span className="text-muted-foreground">Margin (new)</span>
                  <span className={`font-medium text-right ${marginColour}`}>
                    {impact.newMargin.toFixed(1)}% (was {impact.previousMargin.toFixed(1)}%)
                  </span>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={onCancel} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleConfirm} disabled={isSaving}>
              {isSaving ? 'Saving…' : 'Confirm & Save Revision'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Target Resolution Dialog (second step) */}
      <CascadeTargetResolutionDialog
        open={showTargetResolution}
        cascadeSettings={cascadeSettings}
        onResolved={handleTargetResolved}
        onCancel={handleTargetCancelled}
      />
    </>
  );
}
